import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import {
  StepType,
  PlanType,
  PaymentMethod,
  BusinessRole,
  Professional,
  CustomerJourneyState,
  ActionType,
  CustomerJourneyAction
} from './types';
import { emptySchedule } from './utils';
import { useClient } from '@/hooks';
import {
  EmptyObject,
  ErrorI,
  PostOfflinePaymentsI,
  PostSoftStaffI,
  PostVendorInfoI,
  PostWorkingHoursI,
  WorkingHoursI
} from '@/types/cyclone/requests';
import { OfflinePaymentTypeE, VendorI } from '@/types/cyclone/models';
import { useAuth } from '@/contexts';

// Simplified persistence system
const STORAGE_KEY = 'customerJourneyState';
const PROFESSIONALS_BACKUP_KEY = 'cj_professionals_backup';

// Utility function to check time overlaps (duplicated to avoid circular dependencies)
const timeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const hasTimeOverlap = (day: string, slots: { start: string; end: string }[]) => {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i];
      const b = slots[j];

      const aStart = timeToMinutes(a.start);
      const aEnd = timeToMinutes(a.end);
      const bStart = timeToMinutes(b.start);
      const bEnd = timeToMinutes(b.end);

      if ((aStart <= bStart && bStart < aEnd) || (bStart <= aStart && aStart < bEnd)) {
        return true;
      }
    }
  }
  return false;
};

// Robust state persistence
const saveState = (state: CustomerJourneyState): void => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(STORAGE_KEY, serializedState);

    // Always backup professionals separately
    if (state.professionals.length > 0) {
      localStorage.setItem(PROFESSIONALS_BACKUP_KEY, JSON.stringify(state.professionals));
    }
  } catch (error) {
    // Failed to save state
  }
};

const loadState = (): CustomerJourneyState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure professionals array exists
      if (!parsed.professionals) {
        parsed.professionals = [];
      }
      return parsed;
    }
    return null;
  } catch (error) {
    return null;
  }
};

const clearState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PROFESSIONALS_BACKUP_KEY);
  } catch (error) {
    // Failed to clear state
  }
};

// Backup professionals before risky operations
const backupProfessionals = (professionals: Professional[]): void => {
  try {
    if (professionals.length > 0) {
      localStorage.setItem(PROFESSIONALS_BACKUP_KEY, JSON.stringify(professionals));
    }
  } catch (error) {
    // Failed to backup professionals
  }
};

// Estado inicial
const initialState: CustomerJourneyState = {
  currentStep: StepType.PAYMENT_METHODS,
  plan: PlanType.PRO,
  paymentMethods: [],
  cashInstructions: '',
  transferInstructions: '',
  businessRole: null,
  professionals: [],
  planProfessionalsCount: 0,
  stepsCompleted: {
    [StepType.PAYMENT_METHODS]: false,
    [StepType.BUSINESS_ROLE]: false,
    [StepType.TEAM_CREATION]: false,
    [StepType.WORKING_HOURS]: false,
    [StepType.FIRST_SERVICE]: false
  },
  stepsVisible: {
    [StepType.PAYMENT_METHODS]: true,
    [StepType.BUSINESS_ROLE]: false,
    [StepType.TEAM_CREATION]: false,
    [StepType.WORKING_HOURS]: true,
    [StepType.FIRST_SERVICE]: true
  }
};

const getInitialState = (): CustomerJourneyState => {
  const storedState = loadState();
  if (storedState) {
    return storedState;
  }

  // If no stored state, initialize with default professional for PRO plan
  const defaultState = { ...initialState };
  if (defaultState.plan === PlanType.PRO) {
    defaultState.professionals = generateInitialProfessionals(BusinessRole.ADMIN_VENDOR, 1);
  }

  return defaultState;
};

const generateInitialProfessionals = (role: BusinessRole, count: number): Professional[] => {
  return Array.from({ length: count }, (_, i) => ({
    id: `pro-${i + 1}`,
    cyclone_id: null,
    first_name: 'Profesional',
    last_name: `${i + 1}`,
    isAdmin: role === BusinessRole.ADMIN_VENDOR && i === 0,
    hasWorkingHours: false,
    schedule: { ...emptySchedule }
  }));
};

const arePaymentMethodsComplete = (
  methods: PaymentMethod[],
  cashInstr: string,
  transferInstr: string
): boolean => {
  if (methods.length === 0) return false;

  const hasCash = methods.includes(PaymentMethod.CASH);
  const hasTransfer = methods.includes(PaymentMethod.TRANSFER);
  const hasCard = methods.includes(PaymentMethod.CARD);

  if (hasCard && !hasCash && !hasTransfer) return true;

  return (!hasCash || cashInstr.trim().length > 0) && (!hasTransfer || transferInstr.trim().length > 0);
};

// Reducer
const customerJourneyReducer = (
  state: CustomerJourneyState,
  action: CustomerJourneyAction
): CustomerJourneyState => {
  let newState: CustomerJourneyState;

  switch (action.type) {
    case ActionType.SET_STEP:
      newState = {
        ...state,
        currentStep: action.payload
      };
      break;

    case ActionType.SET_PLAN: {
      const newPlan = action.payload;
      const isBusinessPlan = newPlan === PlanType.BUSINESS;

      // Only reset professionals if the plan is actually changing or if no professionals exist
      let updatedProfessionals = state.professionals;

      if (state.plan !== newPlan) {
        // Plan is changing - reset professionals appropriately
        updatedProfessionals = isBusinessPlan
          ? []
          : generateInitialProfessionals(BusinessRole.ADMIN_VENDOR, 1);
      } else if (state.professionals.length === 0) {
        // Same plan but no professionals exist - initialize them
        updatedProfessionals = isBusinessPlan
          ? []
          : generateInitialProfessionals(BusinessRole.ADMIN_VENDOR, 1);
      }
      // If plan is the same and professionals exist, keep them

      newState = {
        ...state,
        plan: newPlan,
        stepsVisible: {
          ...state.stepsVisible,
          [StepType.BUSINESS_ROLE]: isBusinessPlan,
          [StepType.TEAM_CREATION]: isBusinessPlan
        },
        professionals: updatedProfessionals
      };
      break;
    }

    case ActionType.TOGGLE_PAYMENT_METHOD: {
      const method = action.payload;
      const newPaymentMethods = state.paymentMethods.includes(method)
        ? state.paymentMethods.filter((m) => m !== method)
        : [...state.paymentMethods, method];

      const isComplete = arePaymentMethodsComplete(
        newPaymentMethods,
        state.cashInstructions,
        state.transferInstructions
      );

      newState = {
        ...state,
        paymentMethods: newPaymentMethods,
        stepsCompleted: {
          ...state.stepsCompleted,
          [StepType.PAYMENT_METHODS]: isComplete
        }
      };
      break;
    }

    case ActionType.SET_CASH_INSTRUCTIONS: {
      const cashInstructions = action.payload;
      const isComplete = arePaymentMethodsComplete(
        state.paymentMethods,
        cashInstructions,
        state.transferInstructions
      );

      newState = {
        ...state,
        cashInstructions,
        stepsCompleted: {
          ...state.stepsCompleted,
          [StepType.PAYMENT_METHODS]: isComplete
        }
      };
      break;
    }

    case ActionType.SET_TRANSFER_INSTRUCTIONS: {
      const transferInstructions = action.payload;
      const isComplete = arePaymentMethodsComplete(
        state.paymentMethods,
        state.cashInstructions,
        transferInstructions
      );

      newState = {
        ...state,
        transferInstructions,
        stepsCompleted: {
          ...state.stepsCompleted,
          [StepType.PAYMENT_METHODS]: isComplete
        }
      };
      break;
    }

    case ActionType.SET_BUSINESS_ROLE: {
      const businessRole = action.payload;
      const existingAdmin = state.professionals.find((p) => p.isAdmin);

      let initialProfessionals = generateInitialProfessionals(
        businessRole,
        state.planProfessionalsCount || 1
      );

      if (
        existingAdmin &&
        Object.values(existingAdmin.schedule).some((slots) => Array.isArray(slots) && slots.length > 0)
      ) {
        if (businessRole === BusinessRole.ADMIN_VENDOR && initialProfessionals.length > 0) {
          initialProfessionals = [
            {
              ...initialProfessionals[0],
              schedule: existingAdmin.schedule,
              hasWorkingHours: existingAdmin.hasWorkingHours
            },
            ...initialProfessionals.slice(1)
          ];
        }
      }

      newState = {
        ...state,
        businessRole,
        professionals: initialProfessionals,
        stepsCompleted: {
          ...state.stepsCompleted,
          [StepType.BUSINESS_ROLE]: true,
          [StepType.TEAM_CREATION]: initialProfessionals.length > 0
        }
      };
      break;
    }

    case ActionType.ADD_PROFESSIONAL: {
      const updatedProfessionals = [...state.professionals, action.payload];

      newState = {
        ...state,
        professionals: updatedProfessionals,
        stepsCompleted: {
          ...state.stepsCompleted,
          [StepType.TEAM_CREATION]: updatedProfessionals.length > 0
        }
      };
      break;
    }

    case ActionType.REMOVE_PROFESSIONAL: {
      const updatedProfessionals = state.professionals.filter((pro) => pro.id !== action.payload);

      newState = {
        ...state,
        professionals: updatedProfessionals,
        stepsCompleted: {
          ...state.stepsCompleted,
          [StepType.TEAM_CREATION]: updatedProfessionals.length > 0
        }
      };
      break;
    }

    case ActionType.UPDATE_PROFESSIONAL: {
      const updatedProfessionals = state.professionals.map((pro) =>
        pro.id === action.payload.id ? action.payload : pro
      );

      newState = {
        ...state,
        professionals: updatedProfessionals
      };
      break;
    }

    case ActionType.UPDATE_PROFESSIONALS:
      newState = {
        ...state,
        professionals: action.payload
      };
      break;

    case ActionType.SET_WORKING_HOURS: {
      const { professionalId, hasWorkingHours } = action.payload;
      const updatedProfessionals = state.professionals.map((pro) =>
        pro.id === professionalId ? { ...pro, hasWorkingHours } : pro
      );

      const allProfessionalsHaveHours = updatedProfessionals.every((pro) => pro.hasWorkingHours);
      const workingHoursComplete =
        state.plan === PlanType.BUSINESS
          ? allProfessionalsHaveHours && updatedProfessionals.length > 0
          : hasWorkingHours;

      newState = {
        ...state,
        professionals: updatedProfessionals,
        stepsCompleted: {
          ...state.stepsCompleted,
          [StepType.WORKING_HOURS]: workingHoursComplete
        }
      };
      break;
    }

    case ActionType.SET_PLAN_PROFESSIONALS_COUNT:
      newState = {
        ...state,
        planProfessionalsCount: action.payload
      };
      break;

    default:
      newState = state;
      break;
  }

  saveState(newState);
  return newState;
};

// Creación del Contexto
interface CustomerJourneyContextType {
  state: CustomerJourneyState;
  dispatch: React.Dispatch<CustomerJourneyAction>;
  canProceedToNextStep: () => boolean;
  proceedToNextStep: () => void;
  handleBusinessRoleMutation: (role: BusinessRole) => void;
  goToStep: (step: StepType) => void;
  getVisibleSteps: () => StepType[];
  isSaving: boolean;
  clearPersistedState: () => void;
  mutationResetWallet: any;
}

const CustomerJourneyContext = createContext<CustomerJourneyContextType | undefined>(undefined);

// Provider Component
export const CustomerJourneyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { client } = useClient();
  const { reloadSession } = useAuth();
  const [state, dispatch] = useReducer(customerJourneyReducer, getInitialState());
  const navigate = useNavigate();
  const location = useLocation();

  // Simple initialization check
  const isInitializedRef = useRef(false);

  // Initialize state once on mount
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;

      // Always backup current professionals if they exist
      if (state.professionals.length > 0) {
        backupProfessionals(state.professionals);
      }
    }
  }, [state.professionals]);

  // Obtener los pasos visibles según la configuración actual
  const getVisibleSteps = (): StepType[] => {
    return Object.entries(state.stepsVisible)
      .filter(([_, isVisible]) => isVisible) // eslint-disable-line @typescript-eslint/no-unused-vars
      .map(([step]) => parseInt(step, 10) as StepType)
      .sort((a, b) => a - b);
  };

  const advanceToNextStep = () => {
    const visibleSteps = getVisibleSteps();
    const currentIndex = visibleSteps.indexOf(state.currentStep);
    if (currentIndex < visibleSteps.length - 1) {
      const nextStep = visibleSteps[currentIndex + 1];
      dispatch({ type: ActionType.SET_STEP, payload: nextStep });
    }
  };

  const handleMutationError = (error: ErrorI) => {
    toast.error(`Error al guardar: ${error.message || 'Error desconocido'}`);
  };

  const paymentMethodsMutation = useMutation<EmptyObject, ErrorI, PostOfflinePaymentsI>(
    (data) => client<EmptyObject>('wallet/offline_payments', 'POST', { isAuthRequired: true, data }),
    {
      onSuccess: () => {
        reloadSession();
        advanceToNextStep();
      },
      onError: handleMutationError
    }
  );

  const mutationResetWallet = useMutation<EmptyObject, ErrorI, VendorI>(
    (vendor) =>
      client<EmptyObject>(`vendors/${vendor.id}/reset_wallet`, 'POST', {
        isAuthRequired: true
      }),
    {
      onSuccess: () => {
        toast.success('Medios de cobro actualizados');
        reloadSession();
      }
    }
  );

  const businessRoleMutation = useMutation<Partial<PostVendorInfoI>, ErrorI>(
    (vendor) => client(`me/vendor/info`, 'PUT', { isAuthRequired: true, data: vendor }),
    {
      onSuccess: advanceToNextStep,
      onError: handleMutationError
    }
  );

  const teamMutation = useMutation<VendorI, ErrorI, PostSoftStaffI>(
    (data) => client<VendorI>('venues/staff/batch', 'POST', { isAuthRequired: true, data }),
    {
      onSuccess: (data: VendorI) => {
        const staffs = data.venue?.vendors;
        if (staffs) {
          const updatedProfessionals = state.professionals.map((pro) => {
            const staff = staffs.find(
              (s) => s.user.first_name === pro.first_name && s.user.last_name === pro.last_name
            );
            return { ...pro, cyclone_id: staff?.id?.toString() ?? null };
          });
          dispatch({ type: ActionType.UPDATE_PROFESSIONALS, payload: updatedProfessionals });
        }
        advanceToNextStep();
      },
      onError: handleMutationError
    }
  );

  const workingHoursMutation = useMutation<EmptyObject, ErrorI, PostWorkingHoursI>(
    (data) => client<EmptyObject>('me/vendor/working_hours', 'POST', { isAuthRequired: true, data }),
    {
      onSuccess: () => {
        reloadSession();
        advanceToNextStep();
      },
      onError: handleMutationError
    }
  );

  // Actualizar URL cuando cambia el paso actual
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentStepParam = searchParams.get('step');
    const currentStepNumber = currentStepParam ? parseInt(currentStepParam, 10) : null;

    if (currentStepNumber !== state.currentStep) {
      navigate(`/setup?step=${state.currentStep}`, { replace: true });
    }
  }, [state.currentStep, navigate, location.search]);

  const handleBusinessRoleMutation = (role: BusinessRole) => {
    businessRoleMutation.mutate({ is_venue_owner_admin: role !== BusinessRole.ADMIN_VENDOR } as any);
  };

  // Verificar si se puede avanzar al siguiente paso
  const canProceedToNextStep = (): boolean => {
    const currentStepCompleted = state.stepsCompleted[state.currentStep];
    const isMutating =
      paymentMethodsMutation.isLoading ||
      businessRoleMutation.isLoading ||
      teamMutation.isLoading ||
      workingHoursMutation.isLoading;

    // For WorkingHours step, also check for conflicts
    if (state.currentStep === StepType.WORKING_HOURS) {
      const hasConflicts = state.professionals.some((professional) => {
        if (!professional.schedule) return false;
        return Object.entries(professional.schedule).some(([dayKey, slots]) => {
          return hasTimeOverlap(dayKey, slots);
        });
      });

      if (hasConflicts) {
        return false;
      }
    }

    return currentStepCompleted && !isMutating;
  };

  // Preparar solicitud de métodos de pago offline
  const prepareOfflinePaymentsRequest = (): { offline_payments: any[] } => {
    const request = [];

    if (state.paymentMethods.includes(PaymentMethod.CASH)) {
      request.push({
        description: state.cashInstructions,
        discount: 0,
        type: OfflinePaymentTypeE.CASH
      });
    }

    if (state.paymentMethods.includes(PaymentMethod.TRANSFER)) {
      request.push({
        description: state.transferInstructions,
        discount: 0,
        type: OfflinePaymentTypeE.BANK_TRANSFER
      });
    }

    return { offline_payments: request };
  };

  // Procesar horarios de trabajo
  const processWorkingHours = (): PostWorkingHoursI[] => {
    const dayToNumberMap: Record<string, number> = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 0
    };

    const requests: PostWorkingHoursI[] = [];

    state.professionals.forEach((professional) => {
      if (!professional.hasWorkingHours) return;

      const workingHoursRequest: WorkingHoursI[] = [];

      Object.entries(professional.schedule).forEach(([dayKey, timeSlotData]) => {
        const timeSlots = Array.isArray(timeSlotData) ? timeSlotData : [timeSlotData];
        const dayNumber = dayToNumberMap[dayKey as string];

        timeSlots.forEach((timeSlot) => {
          if (!timeSlot?.start || !timeSlot?.end) return;

          const existingDayIndex = workingHoursRequest.findIndex((item) => item.day === dayNumber);

          if (existingDayIndex >= 0) {
            workingHoursRequest[existingDayIndex].schedules.push({
              start_time: timeSlot.start,
              end_time: timeSlot.end
            });
          } else {
            workingHoursRequest.push({
              day: dayNumber,
              schedules: [
                {
                  start_time: timeSlot.start,
                  end_time: timeSlot.end
                }
              ]
            });
          }
        });
      });

      requests.push({
        working_hours: workingHoursRequest,
        vendor_id: professional.cyclone_id?.toString()
      });
    });

    return requests;
  };

  const proceedToNextStep = async (): Promise<void> => {
    if (!canProceedToNextStep()) return;

    switch (state.currentStep) {
      case StepType.PAYMENT_METHODS:
        paymentMethodsMutation.mutate(prepareOfflinePaymentsRequest());
        break;

      case StepType.BUSINESS_ROLE:
        if (state.businessRole) {
          const isVenueOwnerAdmin = state.businessRole !== BusinessRole.ADMIN_VENDOR;
          businessRoleMutation.mutate({ is_venue_owner_admin: isVenueOwnerAdmin } as any);
        }
        break;

      case StepType.TEAM_CREATION: {
        const filteredProfessionals =
          state.businessRole === BusinessRole.ADMIN_VENDOR
            ? state.professionals.filter((p) => !p.isAdmin)
            : state.professionals;

        const request = filteredProfessionals.map((pro) => ({
          first_name: pro.first_name,
          last_name: pro.last_name
        }));

        teamMutation.mutate(request as any);
        break;
      }

      case StepType.WORKING_HOURS:
        processWorkingHours().forEach((request) => {
          workingHoursMutation.mutate(request);
        });
        break;

      default:
        advanceToNextStep();
        break;
    }
  };

  const goToStep = (step: StepType): void => {
    const visibleSteps = getVisibleSteps();
    if (visibleSteps.includes(step)) {
      dispatch({ type: ActionType.SET_STEP, payload: step });
    }
  };

  const isMutating =
    paymentMethodsMutation.isLoading ||
    businessRoleMutation.isLoading ||
    teamMutation.isLoading ||
    workingHoursMutation.isLoading;

  const clearPersistedState = () => {
    clearState();
  };

  const contextValue = {
    state,
    dispatch,
    canProceedToNextStep,
    proceedToNextStep,
    handleBusinessRoleMutation,
    goToStep,
    getVisibleSteps,
    isSaving: isMutating,
    clearPersistedState,
    mutationResetWallet
  };

  return <CustomerJourneyContext.Provider value={contextValue}>{children}</CustomerJourneyContext.Provider>;
};

export const useCustomerJourney = () => {
  const context = useContext(CustomerJourneyContext);
  if (!context) {
    throw new Error('useCustomerJourney must be used within a CustomerJourneyProvider');
  }
  return context;
};
